import React from "react";

import { isLikelyNumericTimestampLabel } from "./detection";
import { FormattedTimestampText } from "./FormattedTimestampText";
import { formatTimestamp, TimestampFormat } from "./timeFormat";

/**
 * Decide whether a markdown link's label should be preserved as-authored
 * or overridden with the user's chosen TimestampFormat.
 *
 * A label is REPLACED when it is missing or when it looks like a
 * numeric/timestamp rendering (e.g. `1771481251.468476035`, `t=…ns`,
 * `+1771481426.209115923`). Word labels like `start of segment` pass
 * through untouched.
 *
 * Two callers, two return shapes:
 *
 *   - {@link pickDisplayLabelNode} returns a ReactNode, wrapping in
 *     FormattedTimestampText so the display stays in sync with the
 *     user's chosen TimestampFormat. Used by Timestamp inside an `<a>`.
 *   - {@link pickDisplayLabelString} returns a plain string for consumers
 *     (domain link renderers) whose APIs accept `text: string`.
 */

function shouldReplaceLabel(label: React.ReactNode | undefined): boolean {
  if (label === undefined || label === null) {
    return true;
  }
  if (typeof label !== "string") {
    return false;
  }
  return isLikelyNumericTimestampLabel(label);
}

export function pickDisplayLabelNode(
  label: React.ReactNode,
  anchorNs: bigint,
): React.ReactNode {
  return shouldReplaceLabel(label) ? (
    <FormattedTimestampText ns={anchorNs} />
  ) : (
    label
  );
}

export function pickDisplayLabelString(
  label: string,
  anchorNs: bigint | undefined,
  format: TimestampFormat,
): string {
  if (anchorNs === undefined) {
    return label;
  }
  if (!isLikelyNumericTimestampLabel(label)) {
    return label;
  }
  return formatTimestamp(anchorNs, format);
}
